/**
 * 将 token 序列中的 p.m. / a.m.（含可选英文弯引号或直引号包裹）合并为单个 token，
 * 便于整段作为「只展示不输入」处理（与 word_en 里 "|\"p.m.\"|" 等业务数据对齐）。
 */

const OPEN_QUOTES = new Set(['"', '\u201c', '\u2018'])
const CLOSE_QUOTES = new Set(['"', '\u201d', '\u2019'])

/** 与英文句号等价的点号（兼容全角句点、兼容书写） */
function isDotLike (tok) {
  const s = String(tok ?? '')
  return s === '.' || s === '\uff0e' || s === '\u3002'
}

function matchPmOrAmAt (tokens, startIdx) {
  const n = tokens.length
  let j = startIdx

  let opened = false
  if (j < n && OPEN_QUOTES.has(tokens[j])) {
    opened = true
    j++
  }

  if (j + 3 >= n) return null

  const letter = String(tokens[j] || '').toLowerCase()
  if (letter !== 'p' && letter !== 'a') return null
  if (!isDotLike(tokens[j + 1])) return null
  if (String(tokens[j + 2] || '').toLowerCase() !== 'm') return null
  if (!isDotLike(tokens[j + 3])) return null

  let end = j + 4
  if (opened && end < n && CLOSE_QUOTES.has(tokens[end])) {
    end++
  }

  return {
    endIdx: end,
    display: tokens.slice(startIdx, end).join('')
  }
}

/**
 * @param {string[]} tokens ModePlay.tokenizeEnglish 的产物
 * @returns {string[]}
 */
export function mergeClockAbbreviationTokens (tokens) {
  if (!Array.isArray(tokens) || !tokens.length) return tokens || []
  const out = []
  let i = 0
  while (i < tokens.length) {
    let hit = null
    if (OPEN_QUOTES.has(tokens[i])) {
      hit = matchPmOrAmAt(tokens, i)
    } else {
      const low = String(tokens[i] || '').toLowerCase()
      if (low === 'p' || low === 'a') {
        hit = matchPmOrAmAt(tokens, i)
      }
    }
    if (hit) {
      out.push(hit.display)
      i = hit.endIdx
    } else {
      out.push(tokens[i])
      i++
    }
  }
  return out
}

/**
 * 将 tokenizeEnglish 拆开的千分位数字（100 + , + 000）合并回 100,000，
 * 与 word_en 槽位「100,000」及 en 整句分词对齐。
 * @param {string[]} tokens
 * @returns {string[]}
 */
export function mergeCommaSeparatedNumberTokens (tokens) {
  if (!Array.isArray(tokens) || !tokens.length) return tokens || []
  const out = []
  let i = 0
  while (i < tokens.length) {
    const tok = String(tokens[i] ?? '')
    if (/^\d+$/.test(tok)) {
      let merged = tok
      let j = i + 1
      while (
        j + 1 < tokens.length &&
        tokens[j] === ',' &&
        /^\d{3}$/.test(String(tokens[j + 1] ?? ''))
      ) {
        merged += String(tokens[j]) + String(tokens[j + 1])
        j += 2
      }
      if (j > i + 1) {
        out.push(merged)
        i = j
        continue
      }
    }
    out.push(tokens[i])
    i++
  }
  return out
}

/** 游戏 en / word_en 共用：时钟缩写 + 千分位数字合并 */
export function mergeEnglishGameTokens (tokens) {
  return mergeCommaSeparatedNumberTokens(mergeClockAbbreviationTokens(tokens))
}

function normalizeDotsForClock (s) {
  return String(s || '')
    .replace(/\u3002/g, '.')
    .replace(/\uff0e/g, '.')
}

/**
 * layoutSlots 用：合并后的整段是否应视为「时钟缩写固定展示」，不占字母输入槽。
 * 兼容：引号 / 弯引号 / 括号包裹、全角句点、大小写（P.M.）
 */
export function isFixedClockAbbrevDisplayToken (raw) {
  let t = normalizeDotsForClock(String(raw ?? '').trim())
  if (!t) return false

  t = t
    .replace(/^[\u201c\u2018"'(\[\{（]+/, '')
    .replace(/[\u201d\u2019"')\]\}）]+$/, '')
    .trim()

  return /^p\.m\.$/i.test(t) || /^a\.m\.$/i.test(t)
}
